import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Eye } from "lucide-react";
import { FormEvent, useState } from "react";

type VerifyDetailsFormValues = {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
};

const initialFormValues: VerifyDetailsFormValues = {
  username: "",
  email: "",
  password: "",
  confirmPassword: "",
};

export function MvpDetailsVerifyForm({
  isFormSubmitting,
  onContinueClick,
  usernameLabelOverride,
  emailLabelOverride,
  passwordLabelOverride,
}: Readonly<{
  isFormSubmitting: boolean;
  onContinueClick: (values: VerifyDetailsFormValues) => Promise<void>;
  usernameLabelOverride?: string;
  emailLabelOverride?: string;
  passwordLabelOverride?: string;
}>) {
  const [values, setValues] = useState<VerifyDetailsFormValues>(initialFormValues);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleFieldChange = (field: keyof VerifyDetailsFormValues, value: string) => {
    setValues((previousValues) => ({
      ...previousValues,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!values.username.trim()) {
      setError("Username is required.");
      return;
    }

    if (!values.email.trim()) {
      setError("Email is required.");
      return;
    }

    if (!values.password.trim()) {
      setError("Password is required.");
      return;
    }

    if (!values.confirmPassword.trim()) {
      setError("Confirm password is required.");
      return;
    }

    if (values.password !== values.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setError("");
    await onContinueClick(values);
  };

  return (
    <Card className="w-full order-1 xl:order-2 bg-white rounded-md z-10 shadow-lg h-fit p-7">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">{usernameLabelOverride ? `${usernameLabelOverride} *` : "Username *"}</label>
            <Input
              type="text"
              placeholder={`Enter your ${usernameLabelOverride ? `${usernameLabelOverride.toLowerCase()}` : "username"}`}
              value={values.username}
              onChange={(event) => handleFieldChange("username", event.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{emailLabelOverride ? `${emailLabelOverride} *` : "Email *"}</label>
            <Input
              type="email"
              placeholder={`Enter your ${emailLabelOverride ? `${emailLabelOverride.toLowerCase()}` : "email"}`}
              value={values.email}
              onChange={(event) => handleFieldChange("email", event.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">{passwordLabelOverride ? `${passwordLabelOverride} *` : "Password *"}</label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder={`Enter your ${passwordLabelOverride ? `${passwordLabelOverride.toLowerCase()}` : "password"}`}
                value={values.password}
                onChange={(event) => handleFieldChange("password", event.target.value)}
              />
              <button
                type="button"
                className={cn(
                  "absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600",
                )}
                onMouseDown={() => setShowPassword(true)}
                onMouseUp={() => setShowPassword(false)}
                onMouseLeave={() => setShowPassword(false)}
                onTouchStart={() => setShowPassword(true)}
                onTouchEnd={() => setShowPassword(false)}
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{passwordLabelOverride ? `Confirm ${passwordLabelOverride} *` : "Confirm Password *"}</label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                placeholder={`Confirm your ${passwordLabelOverride ? `${passwordLabelOverride.toLowerCase()}` : "password"}`}
                value={values.confirmPassword}
                onChange={(event) =>
                  handleFieldChange("confirmPassword", event.target.value)
                }
              />
              <button
                type="button"
                className={cn(
                  "absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600",
                )}
                onMouseDown={() => setShowConfirmPassword(true)}
                onMouseUp={() => setShowConfirmPassword(false)}
                onMouseLeave={() => setShowConfirmPassword(false)}
                onTouchStart={() => setShowConfirmPassword(true)}
                onTouchEnd={() => setShowConfirmPassword(false)}
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <Button type="submit" className="w-full mt-2" disabled={isFormSubmitting}>
          Submit
        </Button>
      </form>
    </Card>
  );
}
